import { Component, EventEmitter, Input, Output, ViewChild, OnInit, SimpleChanges } from '@angular/core';
import type { GridApi, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

import { ActionIconsClickEvent, IActionIconCellRendererDef } from 'rev-shared/ui/dataGrid/cellRenderers/ActionIconCellRenderer.Component';
import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { IVbUiDataGridColDef } from 'rev-shared/ui/dataGrid/columns/IVbUiDataGridColDef';
import { VbUiDataGridComponent } from 'rev-shared/ui/dataGrid/VbUiDataGrid.Component';
import { handleTabKeyPress } from 'rev-shared/ui/dataGrid/DataGridUtil';

import { AvailabilityIconCellRendererComponent, IAvailabilityIconCellRendererParams } from './AvailabilityIconCellRenderer.Component';
import { IPresentationProfile } from './EditWebcast.Contract';

import styles from './PresentationProfileInput.module.less';

@Component({
	selector: 'presentation-profile-picker-table',
	templateUrl: './PresentationProfilePickerTable.Component.html',
	standalone: false
})
export class PresentationProfilePickerTableComponent implements OnInit {
	@Input() public presentationProfiles: IPresentationProfile[];
	@Input() public webcastId: string;
	@Input() public value: string;
	@Input() public displayedRows: number;
	@Input() private readonly getStartDate: () => Date;
	@Output() public presentationProfileSelected: EventEmitter<IPresentationProfile> = new EventEmitter();
	@ViewChild(VbUiDataGridComponent) public grid: VbUiDataGridComponent;

	public readonly styles = styles;
	public readonly valueSubject$ = new BehaviorSubject<string>(undefined);
	public readonly AVAILABILITY_CELL_RENDERER_PARAMS: IAvailabilityIconCellRendererParams = {
		selectedProfile$: this.valueSubject$
	};
	public readonly SCHEDULE_CELL_RENDERER_PARAMS: IActionIconCellRendererDef = {
		actions: [{
			title: this.TranslateService.instant('UI_Schedule'),
			iconClass: 'glyphicons calendar',
			buttonClass: 'btn btn-transparent',
			helperIconClass: 'badge',
			getHelperIconText: (data: IPresentationProfile) => data.videoSource.events?.length ? data.videoSource.events.length.toString() : null
		}],
		actionClick: (event: ActionIconsClickEvent) => this.onActionIconClick(event)
	};

	public gridApi: GridApi;
	public gridHeight: string;
	public gridColumnDefs: IVbUiDataGridColDef[];
	public gridComponents: { [p: string]: any; } = {};
	public gridDefaultColDef: any = {};

	constructor(
		private readonly Dialog: DialogService,
		private readonly TranslateService: TranslateService
	) { }

	public ngOnInit(): void {
		this.assignGridSettings();
		this.gridHeight = 50 + (this.displayedRows * 48) + 'px';
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.value) {
			this.valueSubject$.next(changes.value.currentValue);
		}
		if (changes.presentationProfiles) {
			this.setPreselectedProfile();
		}
	}

	public onGridReady(event: GridReadyEvent): void {
		this.gridApi = event.api;
		this.setPreselectedProfile();
	}

	public onRowSelectionChanged(event: SelectionChangedEvent): void {
		const selectedNodes = this.gridApi.getSelectedNodes();
		this.presentationProfileSelected.emit(selectedNodes.length ? selectedNodes[0].data : { id: null });
	}

	private setPreselectedProfile(): void {
		if (this.value) {
			setTimeout(() => {
				this.gridApi?.forEachNode(node => {
					if (node.data.id === this.value) {
						node.setSelected(true);
					}
				});
			}, 0);
		}
	}

	private onActionIconClick(event: ActionIconsClickEvent): void {
		this.viewSchedule(event.rowNode.data);
	}

	private viewSchedule(profile: IPresentationProfile): void {
		this.Dialog.getDialog('EventVideoSourceSchedule').open({
			initialState: {
				reservationDate: this.getStartDate(),
				isSelectedResource: this.webcastId === profile.id,
				videoSourceName: profile.name,
				videoSourceDisplayName: profile.videoSource.displayName,
				events: profile.videoSource.events,
				isVideoSource: true
			}
		});
	}

	private assignGridSettings(): void {
		this.assignGridComponents();
		this.assignGridDefaultColumnDef();
		this.assignGridColumnDefs();
	}

	private assignGridDefaultColumnDef(): void {
		this.gridDefaultColDef = {
			suppressMovable: true,
			suppressKeyboardEvent: handleTabKeyPress
		};
	}

	private assignGridComponents(): void {
		this.gridComponents = {
			availabilityIconRenderer: AvailabilityIconCellRendererComponent
		};
	}

	private assignGridColumnDefs(): void {
		this.gridColumnDefs = [
			{
				checkboxSelection: true,
				width: 40
			},
			{
				headerName: this.TranslateService.instant('Event_PresentationProfile'),
				headerClass: this.styles.headerClass,
				field: 'name'
			},
			{
				headerName: this.TranslateService.instant('Event_Availability'),
				headerClass: this.styles.headerClass,
				cellRenderer: 'availabilityIconRenderer',
				cellRendererParams: this.AVAILABILITY_CELL_RENDERER_PARAMS,
				width: 140
			},
			{
				headerName: this.TranslateService.instant('UI_Schedule'),
				headerClass: this.styles.headerClass,
				cellRenderer: 'actionIcons',
				cellRendererParams: this.SCHEDULE_CELL_RENDERER_PARAMS,
				width: 120
			}
		];
	}

}
